import { formatCurrency } from "@scandotcom/react";
import { IconMapPin } from "@tabler/icons-react";
import React from "react";
import { IconRapid } from "../Icons";
import { LocationResult } from "../NewSearchResults/types";
import { Chip } from "../shared/Chip";
import { StarRating } from "../shared/StarRating";
import { isNil } from "../utils";

interface Props extends LocationResult {
  modalitySlug?: string;
}

export function LocationCard({
  image_src,
  name,
  slug,
  has_next_day_slots,
  open_scanner,
  min_age,
  rating,
  reviews,
  address,
  town,
  price,
  postcode,
  modalitySlug,
}: Props) {
  const formattedAddress = [address, town, postcode].filter(Boolean).join(", ");
  return (
    <article className="grid gap-4 rounded-xl border border-neutral-200 bg-white p-2 sm:grid-cols-[128px,auto,128px]">
      {image_src ? (
        <img
          loading="lazy"
          src={image_src}
          alt={name}
          className="h-48 w-full justify-self-stretch rounded-2xl object-cover object-center max-sm:col-span-full sm:h-28 sm:w-28"
        />
      ) : (
        <div className="h-48 justify-self-stretch rounded-2xl bg-neutral-50 max-sm:col-span-full sm:h-28 sm:w-28"></div>
      )}
      <div>
        <h3 className="font-maison-extended font-bold text-neutral-900">
          <a
            href={`/locations/${slug}`}
            className="line-clamp-2 hover:underline"
          >
            {name}
          </a>
        </h3>

        {Boolean(reviews && rating) && (
          <div data-testid="reviews" className="mb-2">
            <a
              href={`/locations/${slug}/#reviews`}
              className="flex items-center gap-x-1 text-sm font-semibold"
            >
              <StarRating rating={rating} className="h-4" />{" "}
              <span>{rating}</span>
              <span className="text-neutral-500 underline hover:no-underline">
                ({reviews} {reviews === 1 ? "review" : "reviews"})
              </span>
            </a>
          </div>
        )}

        <div
          data-testid="address"
          className="my-2 flex gap-x-1 text-sm text-neutral-600"
        >
          <IconMapPin height={20} className="shrink-0" />

          <address
            className="line-clamp-2 font-normal not-italic"
            title={formattedAddress}
          >
            {formattedAddress}
          </address>
        </div>

        <div data-testid="tags" className="flex flex-wrap gap-1.5">
          {has_next_day_slots && (
            <Chip variant="primary">
              <IconRapid className="mr-1.5 h-5 w-5" />
              Next day appointments
            </Chip>
          )}

          {Boolean(open_scanner) && (
            <Chip variant="secondary">Open scanner</Chip>
          )}

          {!isNil(min_age) && min_age > 0 && (
            <Chip variant="secondary">Min age: {min_age}+</Chip>
          )}
        </div>
      </div>

      <div className="flex flex-wrap justify-between sm:justify-end sm:text-right">
        <div>
          <div className="font-maison-extended text-lg font-bold text-neutral-900">
            {formatCurrency(price / 100)}
          </div>
          <span className="font-medium text-neutral-500">Starting from</span>
        </div>

        <a
          href={`/locations/${slug}?modality=${modalitySlug}`}
          className="button button-primary mt-auto h-fit"
        >
          Book now
        </a>
      </div>
    </article>
  );
}
